import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../ui/Button';
import StatusBanner from '../ui/StatusBanner';
import DirectorPanelState from '../director/DirectorPanelState';
import { apiRequest } from '../../services/apiClient';
import { downloadCsv } from '../../utils/csvExport';
import { buildQuery } from '../../utils/buildQuery';

const GOAL_LIFECYCLE_STATES = ['draft', 'pending', 'approved', 'active', 'returned', 'rejected'];
const REVIEW_DOMAINS = ['goal_proposal', 'flagged_actual', 'attendance_event', 'performance_impact'];

function formatWords(value) {
  return String(value || '')
    .split('_')
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

function getApprovalDomainLabel(value) {
  const labels = {
    flagged_actual: 'Flagged Actual',
    goal: 'Goal Proposal',
    attendance_event: 'Attendance / PTO Event',
    performance_impact: 'Performance Impact'
  };

  return labels[String(value || '').toLowerCase()] || formatWords(value);
}

function getApprovalStatusLabel(value) {
  const labels = {
    pending: 'Pending Review',
    approved: 'Approved',
    denied: 'Denied',
    returned: 'Returned',
    cancelled: 'Cancelled'
  };

  return labels[String(value || '').toLowerCase()] || formatWords(value);
}

function getGoalStatusLabel(value) {
  const labels = {
    draft: 'Draft',
    pending: 'Pending Approval',
    approved: 'Approved',
    active: 'Active',
    returned: 'Returned',
    rejected: 'Rejected'
  };

  return labels[String(value || '').toLowerCase()] || formatWords(value);
}

function getDomainCountMap(items = []) {
  return items.reduce((counts, item) => {
    const key = String(item.approvalDomain || '').toLowerCase();
    counts[key] = (counts[key] || 0) + 1;
    return counts;
  }, {});
}

function getStatusCountMap(items = []) {
  return items.reduce((counts, item) => {
    const key = String(item.status || '').toLowerCase();
    counts[key] = (counts[key] || 0) + 1;
    return counts;
  }, {});
}

function getGoalStatusCountMap(items = []) {
  return items.reduce((counts, item) => {
    const key = String(item.goalStatus || 'active').toLowerCase();
    counts[key] = (counts[key] || 0) + 1;
    return counts;
  }, {});
}

function buildApprovalLocationRows(items = []) {
  const rows = new Map();

  items.forEach((item) => {
    const key = String(item.locationCode || `location-${item.locationId || 'unknown'}`);
    const current = rows.get(key) || {
      locationCode: item.locationCode || 'Unknown',
      locationName: item.locationName || 'Unknown Location',
      total: 0,
      pending: 0,
      approved: 0,
      denied: 0,
      returned: 0
    };

    current.total += 1;
    const statusKey = String(item.status || '').toLowerCase();
    current[statusKey] = (current[statusKey] || 0) + 1;
    rows.set(key, current);
  });

  return Array.from(rows.values()).sort((left, right) => left.locationCode.localeCompare(right.locationCode));
}

function buildGoalLocationRows(items = []) {
  const rows = new Map();

  items.forEach((item) => {
    const key = String(item.locationCode || `location-${item.locationId || 'unknown'}`);
    const current = rows.get(key) || {
      locationCode: item.locationCode || 'Unknown',
      locationName: item.locationName || 'Unknown Location',
      total: 0,
      draft: 0,
      pending: 0,
      approved: 0,
      active: 0,
      returned: 0,
      rejected: 0
    };

    current.total += 1;
    const statusKey = String(item.goalStatus || 'active').toLowerCase();
    current[statusKey] = (current[statusKey] || 0) + 1;
    rows.set(key, current);
  });

  return Array.from(rows.values()).sort((left, right) => left.locationCode.localeCompare(right.locationCode));
}

function buildReviewLocationRows(items = []) {
  const rows = new Map();

  items.forEach((item) => {
    const key = String(item.locationCode || `location-${item.locationId || 'unknown'}`);
    const current = rows.get(key) || {
      locationCode: item.locationCode || 'Unknown',
      locationName: item.locationName || 'Unknown Location',
      total: 0,
      pending: 0,
      approved: 0,
      denied: 0,
      returned: 0,
      flaggedActual: 0,
      attendanceEvent: 0,
      performanceImpact: 0
    };

    current.total += 1;
    const statusKey = String(item.status || '').toLowerCase();
    current[statusKey] = (current[statusKey] || 0) + 1;

    const domainKey = String(item.approvalDomain || '').toLowerCase();
    if (domainKey === 'flagged_actual') {
      current.flaggedActual += 1;
    } else if (domainKey === 'attendance_event') {
      current.attendanceEvent += 1;
    } else if (domainKey === 'performance_impact') {
      current.performanceImpact += 1;
    }

    rows.set(key, current);
  });

  return Array.from(rows.values()).sort((left, right) => left.locationCode.localeCompare(right.locationCode));
}

function buildPendingAging(items = []) {
  const now = Date.now();
  return items.reduce((aging, item) => {
    if (String(item.status || '').toLowerCase() !== 'pending' || !item.submittedAt) {
      return aging;
    }

    const submittedAt = new Date(item.submittedAt).getTime();
    if (!Number.isFinite(submittedAt)) {
      aging.unknown += 1;
      return aging;
    }

    const ageInDays = Math.floor((now - submittedAt) / 86400000);
    if (ageInDays <= 2) {
      aging.zeroToTwo += 1;
    } else if (ageInDays <= 7) {
      aging.threeToSeven += 1;
    } else {
      aging.eightPlus += 1;
    }

    return aging;
  }, {
    zeroToTwo: 0,
    threeToSeven: 0,
    eightPlus: 0,
    unknown: 0
  });
}

async function fetchAllApprovals({ selectedLocationCode }) {
  const limit = 200;
  const rows = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    const qs = buildQuery({
      limit,
      offset,
      locationCode: selectedLocationCode && selectedLocationCode !== 'ALL' ? selectedLocationCode : null
    });

    const response = await apiRequest(`/api/approvals?${qs}`);
    const batch = Array.isArray(response?.groups)
      ? response.groups.flatMap((group) => group.items || [])
      : [];

    rows.push(...batch);
    hasMore = Boolean(response?.hasMore) && batch.length > 0;
    offset += batch.length;
  }

  return rows;
}

function AdminReportingPanel({
  selectedLocationCode = 'ALL',
  locationLabel = 'Company Rollup'
}) {
  const [approvals, setApprovals] = useState([]);
  const [delegations, setDelegations] = useState([]);
  const [goals, setGoals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [banner, setBanner] = useState('');

  const loadReportingData = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const query = selectedLocationCode && selectedLocationCode !== 'ALL'
        ? `?locationCode=${encodeURIComponent(selectedLocationCode)}`
        : '';
      const [approvalRows, delegationRows, goalRows] = await Promise.all([
        fetchAllApprovals({ selectedLocationCode }),
        apiRequest(`/api/delegations${query}`),
        apiRequest(`/api/goals/location${query}`)
      ]);

      setApprovals(Array.isArray(approvalRows) ? approvalRows : []);
      setDelegations(Array.isArray(delegationRows) ? delegationRows : []);
      setGoals(Array.isArray(goalRows) ? goalRows : []);
    } catch (requestError) {
      setError(requestError.message || 'Unable to load reporting data.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedLocationCode]);

  useEffect(() => {
    loadReportingData();
  }, [loadReportingData]);

  const approvalDomainCounts = useMemo(() => getDomainCountMap(approvals), [approvals]);
  const approvalStatusCounts = useMemo(() => getStatusCountMap(approvals), [approvals]);
  const approvalLocationRows = useMemo(() => buildApprovalLocationRows(approvals), [approvals]);
  const pendingAging = useMemo(() => buildPendingAging(approvals), [approvals]);
  const completedApprovals = useMemo(
    () => approvals.filter((item) => String(item.status || '').toLowerCase() !== 'pending'),
    [approvals]
  );
  const directReviewCount = useMemo(
    () => completedApprovals.filter((item) => String(item.metadata?.accessType || 'direct').toLowerCase() !== 'delegated').length,
    [completedApprovals]
  );
  const delegatedReviewCount = useMemo(
    () => completedApprovals.filter((item) => String(item.metadata?.accessType || '').toLowerCase() === 'delegated').length,
    [completedApprovals]
  );

  const goalLifecycleCounts = useMemo(() => getGoalStatusCountMap(goals), [goals]);
  const goalLocationRows = useMemo(() => buildGoalLocationRows(goals), [goals]);
  const pendingGoalCount = Number(goalLifecycleCounts.pending || 0);
  const goalProposalRows = useMemo(
    () => goals.filter((goal) => String(goal.goalStatus || 'active').toLowerCase() !== 'active'),
    [goals]
  );

  const reviewRows = useMemo(
    () => approvals.filter((item) => REVIEW_DOMAINS.includes(String(item.approvalDomain || '').toLowerCase())),
    [approvals]
  );
  const reviewLocationRows = useMemo(() => buildReviewLocationRows(reviewRows), [reviewRows]);

  const handleExportApprovals = useCallback(() => {
    downloadCsv('approval_reporting_export.csv', [
      { label: 'Approval ID', value: 'id' },
      { label: 'Domain', value: (row) => getApprovalDomainLabel(row.approvalDomain) },
      { label: 'Status', value: (row) => getApprovalStatusLabel(row.status) },
      { label: 'Location Code', value: 'locationCode' },
      { label: 'Location Name', value: 'locationName' },
      { label: 'Subject Type', value: 'subjectType' },
      { label: 'Subject ID', value: 'subjectId' },
      { label: 'Submitted By', value: 'submittedByUserEmail' },
      { label: 'Submitted At', value: 'submittedAt' },
      { label: 'Reviewed By', value: 'reviewedByUserEmail' },
      { label: 'Reviewed At', value: 'reviewedAt' },
      { label: 'Access Type', value: (row) => row.metadata?.accessType || 'direct' },
      { label: 'On Behalf Of', value: (row) => row.metadata?.actingOnBehalfOfUserEmail || '' },
      { label: 'Decision Note', value: 'decisionNote' }
    ], approvals);
    setBanner('Approval reporting CSV downloaded.');
  }, [approvals]);

  const handleExportDelegations = useCallback(() => {
    downloadCsv('delegations_export.csv', [
      { label: 'Delegation ID', value: 'id' },
      { label: 'Location Code', value: 'locationCode' },
      { label: 'Location Name', value: 'locationName' },
      { label: 'Permission Type', value: 'authorityType' },
      { label: 'Delegation Mode', value: 'delegationMode' },
      { label: 'Status', value: 'status' },
      { label: 'Delegator', value: 'delegatorUserEmail' },
      { label: 'Delegate', value: 'delegateUserEmail' },
      { label: 'Start At', value: 'startAt' },
      { label: 'End At', value: 'endAt' },
      { label: 'Created At', value: 'createdAt' },
      { label: 'Revoked At', value: 'revokedAt' },
      { label: 'Note', value: 'note' }
    ], delegations);
    setBanner('Delegations CSV downloaded.');
  }, [delegations]);

  const handleExportGoalProposals = useCallback(() => {
    downloadCsv('goal_proposals_export.csv', [
      { label: 'Goal ID', value: 'id' },
      { label: 'Location Code', value: 'locationCode' },
      { label: 'Location Name', value: 'locationName' },
      { label: 'KPI Key', value: 'kpiKey' },
      { label: 'KPI Label', value: 'label' },
      { label: 'Goal Status', value: (row) => getGoalStatusLabel(row.goalStatus) },
      { label: 'Goal Value', value: 'goalValue' },
      { label: 'Period Type', value: 'goalPeriodType' },
      { label: 'Period Start', value: 'periodStart' },
      { label: 'Uploaded By User ID', value: 'uploadedByUserId' },
      { label: 'Uploaded At', value: 'uploadedAt' },
      { label: 'Status Note', value: 'statusNote' }
    ], goalProposalRows);
    setBanner('Goal proposal CSV downloaded.');
  }, [goalProposalRows]);

  return (
    <div className="director-admin-stack">
      <DirectorPanelState kind="loading" message={isLoading ? 'Loading reporting data...' : ''} />
      <DirectorPanelState kind="error" message={error} />
      <DirectorPanelState kind="success" message={banner} />

      <section className="director-card">
        <div className="role-section-header">
          <h2>Reporting Scope</h2>
          <span>{selectedLocationCode === 'ALL' ? 'All Visible Locations' : locationLabel}</span>
        </div>
        <p className="role-section-copy">
          This reporting view summarizes approval activity, goal lifecycle volume, and review workload using the current live governance data.
        </p>
      </section>

      <section className="director-card">
        <div className="role-section-header">
          <h2>Reporting &amp; Export</h2>
          <span>Governance Snapshot</span>
        </div>
        <p className="role-section-copy">
          Use these exports for lightweight review, handoff, or offline analysis without changing the workflow system underneath.
        </p>
        <div className="director-table-actions admin-reporting-export-row">
          <Button size="sm" variant="secondary" onClick={handleExportApprovals} disabled={approvals.length === 0}>
            Export Approvals
          </Button>
          <Button size="sm" variant="secondary" onClick={handleExportDelegations} disabled={delegations.length === 0}>
            Export Delegations
          </Button>
          <Button size="sm" variant="secondary" onClick={handleExportGoalProposals} disabled={goalProposalRows.length === 0}>
            Export Goal Proposals
          </Button>
          <Button size="sm" onClick={loadReportingData} loading={isLoading} loadingLabel="Refreshing…">
            Refresh Reporting
          </Button>
        </div>
      </section>

      <div className="director-status-summary-grid admin-reporting-summary-grid">
        <div className="role-request-summary-card">
          <span>Total Approvals</span>
          <strong>{approvals.length}</strong>
        </div>
        <div className="role-request-summary-card">
          <span>Pending Reviews</span>
          <strong>{approvalStatusCounts.pending || 0}</strong>
        </div>
        <div className="role-request-summary-card">
          <span>Pending Goal Proposals</span>
          <strong>{pendingGoalCount}</strong>
        </div>
        <div className="role-request-summary-card">
          <span>Active Delegations</span>
          <strong>{delegations.filter((delegation) => delegation.status === 'active').length}</strong>
        </div>
      </div>

      <div className="director-secondary-grid admin-reporting-grid">
        <section className="director-card">
          <div className="role-section-header">
            <h2>Approval Reporting</h2>
            <span>{approvals.length} Items</span>
          </div>
          <p className="role-section-copy">
            Counts below use the existing approval engine and include flagged actuals, attendance event reviews, performance-impact reviews, and goal proposals.
          </p>
          <div className="director-status-summary-grid admin-reporting-summary-grid">
            {['goal_proposal', 'flagged_actual', 'attendance_event', 'performance_impact'].map((domain) => (
              <div className="role-request-summary-card" key={domain}>
                <span>{getApprovalDomainLabel(domain)}</span>
                <strong>{approvalDomainCounts[domain] || 0}</strong>
              </div>
            ))}
          </div>
          <div className="director-status-summary-grid admin-reporting-summary-grid">
            {['pending', 'approved', 'denied', 'returned'].map((status) => (
              <div className="role-request-summary-card" key={status}>
                <span>{getApprovalStatusLabel(status)}</span>
                <strong>{approvalStatusCounts[status] || 0}</strong>
              </div>
            ))}
          </div>
          <StatusBanner
            kind="info"
            className="role-inline-message"
            message={(
              <>
                Direct reviews: <strong>{directReviewCount}</strong> | Delegated reviews: <strong>{delegatedReviewCount}</strong> | Pending aging 0-2d: <strong>{pendingAging.zeroToTwo}</strong>, 3-7d: <strong>{pendingAging.threeToSeven}</strong>, 8+d: <strong>{pendingAging.eightPlus}</strong>
              </>
            )}
          />
          <div className="table-card">
            <table className="role-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Total</th>
                  <th>Pending</th>
                  <th>Approved</th>
                  <th>Denied</th>
                  <th>Returned</th>
                </tr>
              </thead>
              <tbody>
                {approvalLocationRows.map((row) => (
                  <tr key={row.locationCode}>
                    <td>{row.locationCode} - {row.locationName}</td>
                    <td>{row.total}</td>
                    <td>{row.pending}</td>
                    <td>{row.approved}</td>
                    <td>{row.denied}</td>
                    <td>{row.returned}</td>
                  </tr>
                ))}
                {approvalLocationRows.length === 0 ? (
                  <tr>
                    <td colSpan="6">No approval activity is available in the current scope yet.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        <section className="director-card">
          <div className="role-section-header">
            <h2>Goal Lifecycle Reporting</h2>
            <span>{goals.length} Goal Rows</span>
          </div>
          <p className="role-section-copy">
            This section summarizes goal lifecycle state volume using the current goal records already stored in the system.
          </p>
          <div className="director-status-summary-grid admin-reporting-summary-grid">
            {GOAL_LIFECYCLE_STATES.map((status) => (
              <div className="role-request-summary-card" key={status}>
                <span>{getGoalStatusLabel(status)}</span>
                <strong>{goalLifecycleCounts[status] || 0}</strong>
              </div>
            ))}
          </div>
          <p className="director-muted">
            Pending proposal volume: <strong>{pendingGoalCount}</strong>
          </p>
          <div className="table-card">
            <table className="role-table">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Total</th>
                  <th>Draft</th>
                  <th>Pending</th>
                  <th>Approved</th>
                  <th>Active</th>
                  <th>Returned</th>
                  <th>Rejected</th>
                </tr>
              </thead>
              <tbody>
                {goalLocationRows.map((row) => (
                  <tr key={row.locationCode}>
                    <td>{row.locationCode} - {row.locationName}</td>
                    <td>{row.total}</td>
                    <td>{row.draft}</td>
                    <td>{row.pending}</td>
                    <td>{row.approved}</td>
                    <td>{row.active}</td>
                    <td>{row.returned}</td>
                    <td>{row.rejected}</td>
                  </tr>
                ))}
                {goalLocationRows.length === 0 ? (
                  <tr>
                    <td colSpan="8">No goal lifecycle rows are available in the current scope.</td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <section className="director-card">
        <div className="role-section-header">
          <h2>Review Volume by Location</h2>
          <span>{reviewRows.length} Review Items</span>
        </div>
        <p className="role-section-copy">
          Review volume includes flagged actuals, attendance event approvals, and performance-impact reviews already flowing through the approval engine.
        </p>
        <div className="table-card">
          <table className="role-table">
            <thead>
              <tr>
                <th>Location</th>
                <th>Total</th>
                <th>Pending</th>
                <th>Approved</th>
                <th>Denied</th>
                <th>Returned</th>
                <th>Flagged Actual</th>
                <th>Attendance Event</th>
                <th>Performance Impact</th>
              </tr>
            </thead>
            <tbody>
              {reviewLocationRows.map((row) => (
                <tr key={row.locationCode}>
                  <td>{row.locationCode} - {row.locationName}</td>
                  <td>{row.total}</td>
                  <td>{row.pending}</td>
                  <td>{row.approved}</td>
                  <td>{row.denied}</td>
                  <td>{row.returned}</td>
                  <td>{row.flaggedActual}</td>
                  <td>{row.attendanceEvent}</td>
                  <td>{row.performanceImpact}</td>
                </tr>
              ))}
              {reviewLocationRows.length === 0 ? (
                <tr>
                  <td colSpan="9">No flagged actual or attendance-related review items are available in the current scope.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export default AdminReportingPanel;
